import React, {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { BREAKPOINTS } from '@depop/web-thrift-ui/theme/default/breakpoints.constants';
import clsx from 'clsx';

import styles from './CondensedProductCard.module.css';

import { MediaCarousel } from '@/app/[locale]/products/[slug]/components/Images/MediaCarousel';
import { useBespokeCondensedPDP } from '@/modules/product/hooks/useBespokeCondensedPDP';

const TWO_XL_MEDIA_QUERY = `(min-width: ${BREAKPOINTS.TWO_XL_MIN_PX})`;
const TWO_XL_SLIDES_VISIBLE = 2;
const SINGLE_SLIDE_VISIBLE = 1;

function subscribeToTwoXl(onStoreChange: () => void) {
  const mediaQuery = window.matchMedia(TWO_XL_MEDIA_QUERY);
  mediaQuery.addEventListener('change', onStoreChange);
  return () => mediaQuery.removeEventListener('change', onStoreChange);
}

function useSlidesVisible() {
  return useSyncExternalStore(
    subscribeToTwoXl,
    () =>
      window.matchMedia(TWO_XL_MEDIA_QUERY).matches
        ? TWO_XL_SLIDES_VISIBLE
        : SINGLE_SLIDE_VISIBLE,
    () => SINGLE_SLIDE_VISIBLE
  );
}

type Props = {
  carouselThumbnails: string[];
  productId: number;
  accessory: React.ReactNode;
  children: React.ReactNode;
};

export function DesktopImageGallery({
  carouselThumbnails,
  productId,
  accessory,
  children,
}: Props) {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const thumbnailStripRef = useRef<HTMLDivElement>(null);
  const twoXlSlidesVisible = useSlidesVisible();
  const { isVariant: isBespokeCondensedPDPVariant } = useBespokeCondensedPDP();

  const slidesVisible = isBespokeCondensedPDPVariant
    ? twoXlSlidesVisible
    : SINGLE_SLIDE_VISIBLE;

  const maxIndex = Math.max(0, React.Children.count(children) - slidesVisible);
  const clampedIndex = Math.min(activeImageIndex, maxIndex);

  useEffect(() => {
    const strip = thumbnailStripRef.current;
    const activeThumbnail = strip?.children[clampedIndex] as HTMLElement;
    if (activeThumbnail?.scrollIntoView) {
      activeThumbnail.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [clampedIndex]);

  return (
    <div
      className={clsx(styles.desktopImages, {
        [styles.desktopImagesVariant]: isBespokeCondensedPDPVariant,
      })}
    >
      <div className={styles.thumbnailStrip} ref={thumbnailStripRef}>
        {carouselThumbnails.map((url, i) => (
          <button
            key={url}
            type="button"
            className={`${styles.thumbnail} ${i >= clampedIndex && i < clampedIndex + slidesVisible ? styles.thumbnailActive : ''}`}
            onClick={() => setActiveImageIndex(Math.min(i, maxIndex))}
          >
            <img src={url} alt="" className={styles.thumbnailImage} />
          </button>
        ))}
      </div>
      <MediaCarousel
        imageThumbnails={carouselThumbnails}
        productId={productId}
        accessory={accessory}
        activeIndex={clampedIndex}
        onIndexChange={setActiveImageIndex}
        slidesVisible={slidesVisible}
        trackImageCarouselIndex={isBespokeCondensedPDPVariant}
      >
        {children}
      </MediaCarousel>
    </div>
  );
}
